"use client";

import { Command as CommandPrimitive } from "cmdk";
import { X } from "lucide-react";
import * as React from "react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { Command, CommandGroup, CommandItem, CommandList, CommandSeparator } from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { cn } from "@/src/lib/shadcn-utils";

type Option = Record<"value" | "label", string>;

interface MultiSelectProps {
  options: Option[];
  value: Option[];
  onChange: (value: Option[]) => void;
  placeholder?: string;
  className?: string;
  maxSelected?: number;
  maxDisplay?: number;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  emptyText?: React.ReactNode;
  showSelectAll?: boolean;
  error?: string;
  tabIndex?: number;
  chipClassName?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select items...",
  className,
  maxSelected,
  maxDisplay = 5,
  disabled,
  loading,
  loadingText = "Loading...",
  emptyText = "No results found",
  showSelectAll = true,
  error,
  tabIndex,
  chipClassName,
  ...props
}: MultiSelectProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const handleUnselect = (option: Option) => {
    onChange(value.filter((s) => s.value !== option.value));
  };

  const handleSelectAll = () => {
    if (maxSelected !== undefined && options.length > maxSelected) {
      onChange(options.slice(0, maxSelected));
    } else {
      onChange(options);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current;

    if (input) {
      if (e.key === "Delete" || e.key === "Backspace") {
        if (input.value === "" && value.length > 0) {
          onChange(value.slice(0, -1));
        }
      }

      if (e.key === "Escape") {
        input.blur();
      }
    }
  };

  const selectables = options.filter((option) => !value.some((selected) => selected.value === option.value));
  const isMaxReached = maxSelected !== undefined && value.length >= maxSelected;
  const allSelected = options.length > 0 && value.length === options.length;
  const hasOptions = options.length > 0;
  const shouldShowSelectAll = showSelectAll && !inputValue && hasOptions;
  const filteredSelectables = inputValue
    ? selectables.filter(
        (option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.value.toLowerCase().includes(inputValue.toLowerCase())
      )
    : selectables;
  const hasFilteredSelectables = filteredSelectables.length > 0;

  if (loading) {
    return (
      <Command className={`relative overflow-visible bg-transparent ${className}`} {...props}>
        <div className="group rounded-md border border-accent bg-background px-3 py-1.5 text-sm shadow-sm opacity-50 cursor-not-allowed">
          <div className="flex items-center text-muted-foreground h-[22px]">{loadingText}</div>
        </div>
      </Command>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Command
        onKeyDown={handleKeyDown}
        className={`relative overflow-visible bg-transparent ${className}`}
        shouldFilter={false}
        {...props}
      >
        <PopoverTrigger asChild>
          <div
            className={cn(
              // py-1.5 makes this closer in size to regular <Selects>, even when selected entity tags are inside
              "group flex items-center rounded-md border bg-background px-3 py-1.5 text-sm shadow-sm ring-offset-background focus-within:ring-1 min-h-[38px] border-accent focus-within:ring-ring",
              disabled ? "opacity-50 cursor-not-allowed" : ""
            )}
          >
            <div className="flex flex-wrap items-center gap-1 min-w-0 w-full">
              {value.slice(0, maxDisplay).map((option) => (
                <Badge key={option.value} variant="secondary" className={cn("max-w-full", chipClassName)}>
                  <span className="truncate">{option.label}</span>
                  <button
                    type="button"
                    className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 flex-shrink-0"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUnselect(option);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();

                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnselect(option);
                    }}
                    disabled={disabled}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              ))}

              {value.length > maxDisplay && (
                <Badge variant="secondary" className={cn("cursor-default flex-shrink-0 max-w-full", chipClassName)}>
                  <span className="truncate">... and {value.length - maxDisplay} more</span>
                </Badge>
              )}

              <div className="flex-1 min-w-0">
                <CommandPrimitive.Input
                  ref={inputRef}
                  value={inputValue}
                  onValueChange={setInputValue}
                  placeholder={isMaxReached ? "Max selections reached" : value.length === 0 ? placeholder : undefined}
                  className="w-full bg-transparent outline-none placeholder:text-foreground h-[22px] text-ellipsis"
                  disabled={isMaxReached || disabled}
                  aria-invalid={!!error}
                  tabIndex={tabIndex}
                  onFocus={() => setOpen(true)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
        </PopoverTrigger>
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

        {/* Disable close animation since dropdown contents change is jarring when pressing select/unselect all */}
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 data-[state=closed]:duration-0"
          sideOffset={5}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <CommandList>
            <CommandGroup key={value.length} className="h-full overflow-y-auto">
              {shouldShowSelectAll && (
                <>
                  <CommandItem
                    onMouseDown={(e) => e.preventDefault()}
                    onSelect={() => {
                      if (allSelected) onChange([]);
                      else handleSelectAll();
                      setOpen(false);
                    }}
                    disabled={disabled}
                  >
                    {allSelected ? "Unselect all" : "Select all"}
                  </CommandItem>
                  {hasFilteredSelectables && <CommandSeparator className="my-1" />}
                </>
              )}
              {hasFilteredSelectables ? (
                filteredSelectables.map((option) => (
                  <CommandItem
                    key={option.value}
                    onMouseDown={(e) => e.preventDefault()}
                    onSelect={() => {
                      if (!isMaxReached) {
                        setInputValue("");

                        onChange([...value, option]);

                        if (value.length + 1 === maxSelected) {
                          setOpen(false);
                        }
                      }
                    }}
                    disabled={isMaxReached}
                  >
                    {option.label}
                  </CommandItem>
                ))
              ) : !shouldShowSelectAll ? (
                <div className="p-2">
                  <p className="text-sm">{emptyText}</p>
                </div>
              ) : null}
            </CommandGroup>
          </CommandList>
        </PopoverContent>
      </Command>
    </Popover>
  );
}
